import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowLeft, Upload, Download, Trash2, FileText, Mail, Send,
  Activity, Calendar, DollarSign, Users, MapPin, Phone,
  Clock, CheckCircle, PauseCircle, XCircle, CheckCircle2,
  File, Image, FileSpreadsheet, Plus, ShoppingCart, Receipt,
  Eye, ChevronDown, AlertTriangle, Ban, Sparkles, Wand2, Loader2, Brain, UserCheck,
  GripVertical, Edit, PlusCircle, Copy, RefreshCw
} from 'lucide-react';
import { jobsAPI, documentsAPI, quotesAPI, contractorsAPI, invoicesAPI, tradeAPI, purchaseOrdersAPI, aiAPI, pricingAPI, quoteTemplatesAPI } from '../services/api';
import api from '../services/api';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DialogTrigger } from '../components/ui/dialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable wrapper components for drag-and-drop
const SortableSection = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

// Job document categories
const jobDocumentTabs = [
  { id: 'general', label: 'General' },
  { id: 'photos', label: 'Photos' },
  { id: 'quotes_variations', label: 'Quotes/Variations' },
  { id: 'reports', label: 'Reports' },
  { id: 'contractor_quotes', label: 'Contractor Quotes' },
  { id: 'purchase_orders', label: 'POs' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'certification_forms', label: 'Certification/Forms' },
  { id: 'emails', label: 'Emails' },
  { id: 'whs', label: 'WH&S' },
];

// Quote section labels
const sectionLabels = {
  preliminaries: 'Preliminaries',
  internal: 'Internal',
  external: 'External',
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  bedroom: 'Bedroom',
  living: 'Living Room',
  laundry: 'Laundry',
  garage: 'Garage',
  outdoor: 'Outdoor',
  custom: 'Custom'
};

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400', icon: Activity },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: 'bg-orange-500/20 text-orange-400', icon: PauseCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  invoiced: { label: 'Invoiced', color: 'bg-purple-500/20 text-purple-400', icon: Receipt },
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-400', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  acknowledged: { label: 'Acknowledged', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle },
};

const getFileIcon = (mimeType, filename) => {
  if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4 text-blue-400" />;
  if (mimeType === 'application/pdf') return <FileText className="h-4 w-4 text-red-400" />;
  if (mimeType?.includes('spreadsheet') || filename?.endsWith('.csv')) return <FileSpreadsheet className="h-4 w-4 text-green-400" />;
  return <File className="h-4 w-4 text-gray-400" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobData, setJobData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeDocTab, setActiveDocTab] = useState('general');
  const [editJobOpen, setEditJobOpen] = useState(false);
  const [editJobData, setEditJobData] = useState({});
  const [savingJob, setSavingJob] = useState(false);

  
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [emails, setEmails] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [tradeTypes, setTradeTypes] = useState([]);
  const [quoteSections, setQuoteSections] = useState([]);
  const [commonPOItems, setCommonPOItems] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState({});
  
  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailData, setEmailData] = useState({ template: '', recipient: '', subject: '', body: '', customMessage: '' });

  // Quote dialog with sections
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteSectionsData, setQuoteSectionsData] = useState([
    { section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit_price: 0, trade_type: 'General' }] }
  ]);
  const [pricingItems, setPricingItems] = useState([]);
  const [selectedPricingCategory, setSelectedPricingCategory] = useState('all');
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Quote view dialog
  const [viewQuoteOpen, setViewQuoteOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Quote editing state
  const [editingQuote, setEditingQuote] = useState(null);
  const [newTradeType, setNewTradeType] = useState('');
  const [addTradeOpen, setAddTradeOpen] = useState(false);

  // Quote variation state
  const [variationOpen, setVariationOpen] = useState(false);
  const [variationQuote, setVariationQuote] = useState(null);
  const [variationType, setVariationType] = useState('budget');
  const [customAdjustment, setCustomAdjustment] = useState('');
  const [variationLoading, setVariationLoading] = useState(false);

  // Invoice view dialog  
  const [viewInvoiceOpen, setViewInvoiceOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // PO from Quote dialog
  const [poOpen, setPoOpen] = useState(false);
  const [poQuote, setPoQuote] = useState(null);
  const [poContractor, setPoContractor] = useState('');
  const [poTradeFilter, setPoTradeFilter] = useState('all');
  const [additionalItems, setAdditionalItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // {itemIndex: {selected: true, quantity: x, split: [{contractor_id, quantity, amount}]}}
  const [itemAllocations, setItemAllocations] = useState([]); // [{itemIndex, contractor_id, quantity, unit_price, description}]

  // PO Cancel dialog
  const [cancelPoOpen, setCancelPoOpen] = useState(false);
  const [cancellingPo, setCancellingPo] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [sendCancelEmail, setSendCancelEmail] = useState(true);

  // AI Features state
  const [aiSuggestOpen, setAiSuggestOpen] = useState(false);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiTradeSuggestions, setAiTradeSuggestions] = useState(null);
  const [aiCostOpen, setAiCostOpen] = useState(false);
  const [aiCostLoading, setAiCostLoading] = useState(false);
  const [aiCostEstimate, setAiCostEstimate] = useState(null);
  const [aiEmailOpen, setAiEmailOpen] = useState(false);
  const [aiEmailLoading, setAiEmailLoading] = useState(false);
  const [aiEmailDraft, setAiEmailDraft] = useState(null);
  const [aiEmailType, setAiEmailType] = useState('quote_followup');
  const [aiEmailRecipient, setAiEmailRecipient] = useState('');

  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseData, setExpenseData] = useState({
    category: 'materials',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    tax_deductible: true,
    gst_included: true,
    notes: '',
  });
  const [expenseReceipt, setExpenseReceipt] = useState(null);
  const [expenseTotals, setExpenseTotals] = useState({});

  const expenseCategories = [
    { id: 'materials', label: 'Materials' },
    { id: 'labor', label: 'Labor' },
    { id: 'equipment', label: 'Equipment Hire' },
    { id: 'travel', label: 'Travel' },
    { id: 'subcontractor', label: 'Subcontractor' },
    { id: 'permits', label: 'Permits & Fees' },
    { id: 'other', label: 'Other' },
  ];

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle drag end for sections
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setQuoteSectionsData((sections) => {
        const oldIndex = sections.findIndex((s, i) => `section-${i}` === active.id);
        const newIndex = sections.findIndex((s, i) => `section-${i}` === over.id);
        return arrayMove(sections, oldIndex, newIndex);
      });
    }
  };

  // Handle drag end for items within a section
  const handleItemDragEnd = (sectionIdx) => (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setQuoteSectionsData((sections) => {
        const updated = [...sections];
        const items = updated[sectionIdx].items;
        const oldIndex = items.findIndex((_, i) => `item-${sectionIdx}-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `item-${sectionIdx}-${i}` === over.id);
        updated[sectionIdx].items = arrayMove(items, oldIndex, newIndex);
        return updated;
      });
    }
  };

  // Handle edit quote
  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    // Load quote sections into editing form
    if (quote.sections && quote.sections.length > 0) {
      setQuoteSectionsData(quote.sections.map(section => ({
        section_type: section.section_type || 'custom',
        section_name: section.section_name || '',
        items: section.items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          trade_type: item.trade_type || 'General',
          unit: item.unit || 'Item',
          buy_rate: item.buy_rate || 0
        }))
      })));
    } else if (quote.items && quote.items.length > 0) {
      // Convert flat items to sections format
      setQuoteSectionsData([{
        section_type: 'custom',
        section_name: 'Items',
        items: quote.items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          trade_type: item.trade_type || 'General',
          unit: item.unit || 'Item',
          buy_rate: item.buy_rate || 0
        }))
      }]);
    }
    setQuoteOpen(true);
  };

  // Handle add custom trade type
  const handleAddTradeType = async () => {
    if (!newTradeType.trim()) {
      toast.error('Please enter a trade name');
      return;
    }
    try {
      await tradeAPI.createTradeType({ name: newTradeType.trim() });
      toast.success(`Trade type "${newTradeType}" added`);
      // Refresh trade types
      const tradesRes = await tradeAPI.getTradeTypes();
      setTradeTypes(tradesRes.data);
      setNewTradeType('');
      setAddTradeOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add trade type');
    }
  };

  // Handle duplicate quote
  const handleDuplicateQuote = async (quote) => {
    try {
      const response = await quotesAPI.duplicate(quote.id);
      toast.success(`Quote duplicated as ${response.data.quote_number}`);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate quote');
    }
  };

  // Handle create revision
  const handleCreateRevision = async (quote) => {
    try {
      const response = await quotesAPI.createRevision(quote.id);
      toast.success(response.data.message);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    }
  };

  // Invoice logging / upload against this job (external invoicing)
  const invFileRef = useRef(null);
  const [logInvOpen, setLogInvOpen] = useState(false);
  const [savingLogInv, setSavingLogInv] = useState(false);
  const [uploadingInv, setUploadingInv] = useState(false);
  const [logInvData, setLogInvData] = useState({ invoice_number: '', invoice_date: '', subtotal: '', gst: '', total: '', notes: '' });

  const handleLogInvoice = async () => {
    const subtotal = parseFloat(logInvData.subtotal) || 0;
    const gst = logInvData.gst === '' ? Math.round(subtotal * 0.1 * 100) / 100 : parseFloat(logInvData.gst) || 0;
    const total = logInvData.total === '' ? Math.round((subtotal + gst) * 100) / 100 : parseFloat(logInvData.total) || 0;
    if (total <= 0) { toast.error('Enter the invoice amount'); return; }
    setSavingLogInv(true);
    try {
      const res = await jobsAPI.logInvoice(jobId, { ...logInvData, subtotal, gst, total });
      toast.success(`Invoice ${res.data.invoice_number} logged — due ${res.data.due_date}`);
      setLogInvOpen(false);
      fetchJobData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log invoice');
    } finally {
      setSavingLogInv(false);
    }
  };

  const handleUploadInvoiceFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInv(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await jobsAPI.uploadInvoice(jobId, fd);
      toast.success(`Invoice ${res.data.invoice_number} read — $${Number(res.data.total).toFixed(2)}, due ${res.data.due_date}${res.data.low_confidence ? ' (please double-check)' : ''}`);
      fetchJobData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to read invoice');
    } finally {
      setUploadingInv(false);
      if (invFileRef.current) invFileRef.current.value = '';
    }
  };

  // Retroactively build a draft quote from this job's PO (for jobs imported before auto-quote)
  const [creatingQuoteFromPO, setCreatingQuoteFromPO] = useState(false);
  const handleCreateQuoteFromPO = async () => {
    try {
      setCreatingQuoteFromPO(true);
      const response = await jobsAPI.createQuoteFromPO(jobId);
      toast.success(`Draft quote ${response.data.quote_number} created from PO`);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote from PO');
    } finally {
      setCreatingQuoteFromPO(false);
    }
  };

  // Open variation dialog
  const openVariationDialog = (quote) => {
    setVariationQuote(quote);
    setVariationType('budget');
    setCustomAdjustment('');
    setVariationOpen(true);
  };

  // Handle generate variation
  const handleGenerateVariation = async () => {
    if (!variationQuote) return;
    
    setVariationLoading(true);
    try {
      const data = {
        type: variationType,
        adjustment_percent: customAdjustment ? parseFloat(customAdjustment) : undefined
      };
      const response = await quotesAPI.generateVariation(variationQuote.id, data);
      
      const diff = response.data.difference;
      const diffPercent = response.data.difference_percent;
      toast.success(
        `Variation created: $${response.data.variation_total.toFixed(2)} (${diffPercent >= 0 ? '+' : ''}${diffPercent.toFixed(1)}%)`
      );
      
      setVariationOpen(false);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate variation');
    } finally {
      setVariationLoading(false);
    }
  };

  const fetchJobData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobRes, contractorsRes, tradesRes, sectionsRes, poItemsRes, templatesRes, pricingRes, quoteTemplatesRes] = await Promise.all([
        jobsAPI.getFull(jobId),
        contractorsAPI.getAll(),
        tradeAPI.getTradeTypes(),
        tradeAPI.getQuoteSections(),
        tradeAPI.getCommonPOItems(),
        tradeAPI.getEmailTemplates(),
        pricingAPI.getAll(),
        quoteTemplatesAPI.getAll()
      ]);
      setJobData(jobRes.data);
      setContractors(contractorsRes.data);
      setTradeTypes(tradesRes.data);
      setQuoteSections(sectionsRes.data);
      setCommonPOItems(poItemsRes.data);
      setEmailTemplates(templatesRes.data);
      setPricingItems(pricingRes.data || []);
      setQuoteTemplates(quoteTemplatesRes.data || []);
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchDocuments = useCallback(async (docType = null) => {
    try {
      const params = docType ? { document_type: docType } : {};
      const response = await jobsAPI.getDocuments(jobId, params);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  }, [jobId]);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await jobsAPI.getActivity(jobId);
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  }, [jobId]);

  const fetchEmails = useCallback(async () => {
    try {
      const response = await jobsAPI.getEmails(jobId);
      setEmails(response.data);
    } catch (error) {
      console.error('Failed to load emails:', error);
    }
  }, [jobId]);

  const fetchExpenses = useCallback(async () => {
    try {
      const response = await api.get(`/jobs/${jobId}/expenses`);
      setExpenses(response.data.expenses || []);
      setExpenseTotals({
        grand_total: response.data.grand_total,
        gst_total: response.data.gst_total,
        net_total: response.data.net_total,
        by_category: response.data.totals_by_category
      });
    } catch (error) {
      console.error('Failed to load expenses:', error);
    }
  }, [jobId]);

  useEffect(() => { fetchJobData(); }, [fetchJobData]);

  useEffect(() => {
    if (activeTab === 'documents') fetchDocuments(activeDocTab);
    else if (activeTab === 'activity') fetchActivity();
    else if (activeTab === 'emails') fetchEmails();
    else if (activeTab === 'expenses') fetchExpenses();
  }, [activeTab, activeDocTab, fetchDocuments, fetchActivity, fetchEmails, fetchExpenses]);

  // Expense handlers
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseData.description || !expenseData.amount) {
      toast.error('Please fill in description and amount');
      return;
    }
    try {
      const data = {
        ...expenseData,
        job_id: jobId,
        amount: parseFloat(expenseData.amount)
      };
      if (expenseReceipt) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          data.receipt_file = reader.result.split(',')[1];
          data.receipt_filename = expenseReceipt.name;
          await api.post(`/jobs/${jobId}/expenses`, data);
          toast.success('Expense added');
          setExpenseOpen(false);
          resetExpenseForm();
          fetchExpenses();
        };
        reader.readAsDataURL(expenseReceipt);
      } else {
        await api.post(`/jobs/${jobId}/expenses`, data);
        toast.success('Expense added');
        setExpenseOpen(false);
        resetExpenseForm();
        fetchExpenses();
      }
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const resetExpenseForm = () => {
    setExpenseData({
      category: 'materials',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      vendor: '',
      tax_deductible: true,
      gst_included: true,
      notes: '',
    });
    setExpenseReceipt(null);
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleExportExpenses = async (format = 'csv') => {
    try {
      const response = await api.get(`/jobs/${jobId}/expenses/export`, {
        params: { format },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${job.title?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Expenses exported');
    } catch (error) {
      toast.error('Failed to export expenses');
    }
  };

  // Upload handlers
  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Please select a file'); return; }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', activeDocTab);
      formData.append('scope', 'job');
      formData.append('linked_entity_type', 'job');
      formData.append('linked_entity_id', jobId);
      if (uploadDescription) formData.append('description', uploadDescription);
      await documentsAPI.upload(formData);
      toast.success('Document uploaded');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchDocuments(activeDocTab);
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (doc) => {
    const link = document.createElement('a');
    const t = localStorage.getItem('token');
    link.href = `${process.env.REACT_APP_BACKEND_URL}${doc.file_path}${t ? `?token=${encodeURIComponent(t)}` : ''}`;
    link.download = doc.original_filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await documentsAPI.delete(doc.id);
      toast.success('Document deleted');
      fetchDocuments(activeDocTab);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Email handling
  const handleSendEmail = async () => {
    if (!emailData.recipient) { toast.error('Recipient email required'); return; }
    try {
      setEmailSending(true);
      await jobsAPI.sendEmail(jobId, {
        template_type: emailData.template || null,
        recipient_email: emailData.recipient,
        subject: emailData.subject,
        body: emailData.body,
        custom_message: emailData.customMessage
      });
      toast.success('Email sent');
      setEmailOpen(false);
      setEmailData({ template: '', recipient: '', subject: '', body: '', customMessage: '' });
      fetchEmails();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleTemplateChange = (template) => {
    if (template && emailTemplates[template] && jobData?.job) {
      const t = emailTemplates[template];
      let subject = t.subject.replace('{job_title}', jobData.job.title).replace('{job_address}', jobData.job.address);
      let body = t.body.replace('{job_title}', jobData.job.title).replace('{job_address}', jobData.job.address).replace('{client_name}', jobData.job.client_name);
      setEmailData({ ...emailData, template, subject, body, recipient: jobData.job.client_email || '' });
    } else {
      setEmailData({ ...emailData, template, subject: `Re: ${jobData?.job?.title} - ${jobData?.job?.address}`, body: '' });
    }
  };

  // Quote section handling
  const addSection = () => {
    setQuoteSectionsData([...quoteSectionsData, { section_type: 'custom', section_name: '', items: [{ description: '', quantity: 1, unit_price: 0, trade_type: 'General' }] }]);
  };

  const removeSection = (sectionIdx) => {
    if (quoteSectionsData.length <= 1) return;
    setQuoteSectionsData(quoteSectionsData.filter((_, i) => i !== sectionIdx));
  };

  const updateSection = (sectionIdx, field, value) => {
    const updated = [...quoteSectionsData];
    updated[sectionIdx][field] = value;
    setQuoteSectionsData(updated);
  };

  const addItemToSection = (sectionIdx) => {
    const updated = [...quoteSectionsData];
    updated[sectionIdx].items.push({ description: '', quantity: 1, unit_price: 0, trade_type: 'General' });
    setQuoteSectionsData(updated);
  };

  const removeItemFromSection = (sectionIdx, itemIdx) => {
    const updated = [...quoteSectionsData];
    if (updated[sectionIdx].items.length <= 1) return;
    updated[sectionIdx].items = updated[sectionIdx].items.filter((_, i) => i !== itemIdx);
    setQuoteSectionsData(updated);
  };

  const updateSectionItem = (sectionIdx, itemIdx, field, value) => {
    const updated = [...quoteSectionsData];
    updated[sectionIdx].items[itemIdx][field] = value;
    setQuoteSectionsData(updated);
  };

  const calculateQuoteTotal = () => {
    let subtotal = 0;
    quoteSectionsData.forEach(section => {
      section.items.forEach(item => {
        subtotal += (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      });
    });
    return { subtotal, tax: subtotal * 0.1, total: subtotal * 1.1 };
  };

  const handleCreateQuote = async () => {
    // Flatten sections to items for API
    const allItems = [];
    quoteSectionsData.forEach(section => {
      section.items.forEach(item => {
        if (item.description) {
          allItems.push({
            ...item,
            section: section.section_type,
            section_name: section.section_name || sectionLabels[section.section_type]
          });
        }
      });
    });

    if (allItems.length === 0) { toast.error('Add at least one item'); return; }

    try {
      if (editingQuote) {
        // Update existing quote
        await quotesAPI.update(editingQuote.id, {
          client_name: jobData.job.client_name,
          client_email: jobData.job.client_email,
          sections: quoteSectionsData,
          items: allItems
        });
        toast.success('Quote updated');
      } else {
        // Create new quote
        await quotesAPI.create({
          job_id: jobId,
          client_name: jobData.job.client_name,
          client_email: jobData.job.client_email,
          sections: quoteSectionsData,
          items: allItems
        });
        toast.success('Quote created');
      }
      setQuoteOpen(false);
      setEditingQuote(null);
      setQuoteSectionsData([{ section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit_price: 0, trade_type: 'General' }] }]);
      fetchJobData();
    } catch (error) {
      toast.error(editingQuote ? 'Failed to update quote' : 'Failed to create quote');
    }
  };

  // Reset quote form when dialog closes
  const handleQuoteDialogClose = (open) => {
    if (!open) {
      setEditingQuote(null);
      setQuoteSectionsData([{ section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit_price: 0, trade_type: 'General' }] }]);
      setSelectedTemplate('');
    }
    setQuoteOpen(open);
  };

  // View quote
  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setViewQuoteOpen(true);
  };

  // View invoice
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewInvoiceOpen(true);
  };

  // Download PDF
  const handleDownloadPdf = async (id, type = 'quote') => {
    try {
      const response = type === 'quote' 
        ? await quotesAPI.downloadPdf(id)
        : await invoicesAPI.downloadPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // PO from Quote
  const openPODialog = (quote) => { 
    // Flatten items from sections if they exist
    let allItems = [];
    if (quote.sections && quote.sections.length > 0) {
      quote.sections.forEach((section, sIdx) => {
        (section.items || []).forEach((item, iIdx) => {
          allItems.push({
            ...item,
            section_name: section.section_name || section.section_type,
            original_index: `${sIdx}-${iIdx}`,
            itemKey: `${sIdx}-${iIdx}` // Unique key for tracking
          });
        });
      });
    } else {
      allItems = (quote.items || []).map((item, idx) => ({ 
        ...item, 
        original_index: idx,
        itemKey: `item-${idx}`
      }));
    }
    
    setPoQuote({ ...quote, flatItems: allItems }); 
    setPoOpen(true); 
    setPoContractor(''); 
    setPoTradeFilter('all'); 
    setAdditionalItems([]);
    setItemAllocations([]);
    setSelectedItems({});
  };

  // Toggle item selection for PO
  const toggleItemSelection = (itemIndex) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemIndex]: prev[itemIndex] ? undefined : true
    }));
  };

  // Get allocated quantity for an item
  const getAllocatedQty = (itemKey) => {
    return itemAllocations
      .filter(a => a.itemKey === itemKey)
      .reduce((sum, a) => sum + (a.quantity || 0), 0);
  };

  // Get remaining quantity for an item
  const getRemainingQty = (item) => {
    const allocated = getAllocatedQty(item.itemKey);
    return (item.quantity || 1) - allocated;
  };

  // Add allocation for an item to a contractor
  const addItemAllocation = (item, remainingQty) => {
    if (!poContractor) {
      toast.error('Select a contractor first');
      return;
    }
    
    if (remainingQty <= 0) {
      toast.error('Item fully allocated');
      return;
    }
    
    const contractor = contractors.find(c => c.id === poContractor);
    
    // Check if same item already allocated to same contractor - if so, just add to quantity
    const existingIdx = itemAllocations.findIndex(
      a => a.itemKey === item.itemKey && a.contractor_id === poContractor
    );
    
    if (existingIdx >= 0) {
      // Add to existing allocation
      setItemAllocations(prev => {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: updated[existingIdx].quantity + remainingQty
        };
        return updated;
      });
      toast.success(`Added ${remainingQty} more to ${contractor?.name}`);
    } else {
      // New allocation
      setItemAllocations(prev => [...prev, {
        itemKey: item.itemKey,
        contractor_id: poContractor,
        contractor_name: contractor?.name || 'Unknown',
        description: item.description,
        trade_type: item.trade_type || 'General',
        quantity: remainingQty,
        unit_price: item.unit_price || 0,
        original_quantity: item.quantity || 1,
        original_price: item.unit_price || 0
      }]);
      toast.success(`Added to ${contractor?.name}`);
    }
  };

  // Update allocation quantity/price
  const updateAllocation = (index, field, value) => {
    setItemAllocations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
      return updated;
    });
  };

  // Remove allocation
  const removeAllocation = (index) => {
    setItemAllocations(prev => prev.filter((_, i) => i !== index));
  };

  // Generate POs from allocations
  const handleGeneratePO = async () => {
    if (itemAllocations.length === 0) {
      toast.error('Add at least one item allocation');
      return;
    }
    
    // Group allocations by contractor
    const byContractor = {};
    itemAllocations.forEach(alloc => {
      if (!byContractor[alloc.contractor_id]) {
        byContractor[alloc.contractor_id] = {
          contractor_id: alloc.contractor_id,
          contractor_name: alloc.contractor_name,
          items: []
        };
      }
      byContractor[alloc.contractor_id].items.push({
        description: alloc.description,
        trade_type: alloc.trade_type,
        quantity: alloc.quantity,
        unit_price: alloc.unit_price
      });
    });
    
    // Add additional items to first contractor or selected contractor
    if (additionalItems.length > 0 && poContractor) {
      if (!byContractor[poContractor]) {
        const contractor = contractors.find(c => c.id === poContractor);
        byContractor[poContractor] = {
          contractor_id: poContractor,
          contractor_name: contractor?.name || 'Unknown',
          items: []
        };
      }
      additionalItems.forEach(item => {
        byContractor[poContractor].items.push({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        });
      });
    }
    
    // Create POs for each contractor
    try {
      const contractorGroups = Object.values(byContractor);
      for (const group of contractorGroups) {
        await quotesAPI.generatePO(poQuote.id, {
          contractor_id: group.contractor_id,
          custom_items: group.items,
          additional_items: []
        });
      }
      toast.success(`Created ${contractorGroups.length} PO(s)`);
      setPoOpen(false);
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const addAdditionalItem = (item) => {
    setAdditionalItems([...additionalItems, { ...item, quantity: 1 }]);
  };

  // Calculate totals by contractor with trade grouping
  const getAllocationTotals = () => {
    const totals = {};
    itemAllocations.forEach(alloc => {
      if (!totals[alloc.contractor_id]) {
        totals[alloc.contractor_id] = { 
          name: alloc.contractor_name, 
          total: 0, 
          items: 0,
          trades: new Set()
        };
      }
      totals[alloc.contractor_id].total += (alloc.quantity * alloc.unit_price);
      totals[alloc.contractor_id].items += 1;
      totals[alloc.contractor_id].trades.add(alloc.trade_type);
    });
    return totals;
  };

  // Calculate budget summary
  const getBudgetSummary = () => {
    const items = poQuote?.flatItems || poQuote?.items || [];
    const quoteTotal = items.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0);
    const allocatedTotal = itemAllocations.reduce((sum, a) => sum + (a.quantity * a.unit_price), 0);
    const unallocatedTotal = quoteTotal - allocatedTotal;
    
    // Count unallocated items
    const unallocatedItems = items.filter(item => getRemainingQty(item) > 0).length;
    const totalItems = items.length;
    const allocatedItems = totalItems - unallocatedItems;
    
    return {
      quoteTotal,
      allocatedTotal,
      unallocatedTotal,
      margin: quoteTotal > 0 ? ((quoteTotal - allocatedTotal) / quoteTotal * 100).toFixed(1) : 0,
      unallocatedItems,
      allocatedItems,
      totalItems,
      isFullyAllocated: unallocatedItems === 0
    };
  };

  // Cancel PO
  const handleCancelPO = async () => {
    if (!cancellingPo) return;
    try {
      await purchaseOrdersAPI.cancel(cancellingPo.id, {
        reason: cancelReason,
        send_email: sendCancelEmail
      });
      toast.success('PO cancelled');
      setCancelPoOpen(false);
      setCancellingPo(null);
      setCancelReason('');
      fetchJobData();
    } catch (error) {
      toast.error('Failed to cancel PO');
    }
  };

  // AI: Suggest Trades for Job
  const handleAISuggestTrades = async () => {
    setAiSuggestLoading(true);
    try {
      const response = await aiAPI.suggestTrades(jobId);
      setAiTradeSuggestions(response.data);
      setAiSuggestOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI suggestions');
    } finally {
      setAiSuggestLoading(false);
    }
  };

  // AI: Get Cost Estimate
  const handleAICostEstimate = async () => {
    setAiCostLoading(true);
    try {
      const response = await aiAPI.estimateCost({
        job_description: `${job?.title || ''} - ${job?.description || ''}`,
        job_type: job?.job_type || 'renovation'
      });
      setAiCostEstimate(response.data);
      setAiCostOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get cost estimate');
    } finally {
      setAiCostLoading(false);
    }
  };

  // AI: Draft Email
  const handleAIDraftEmail = async () => {
    if (!aiEmailRecipient) {
      toast.error('Please enter recipient name');
      return;
    }
    setAiEmailLoading(true);
    try {
      const response = await aiAPI.draftEmail({
        email_type: aiEmailType,
        recipient_name: aiEmailRecipient,
        context: {
          job_title: job?.title || '',
          job_address: job?.address || '',
          client_name: job?.client_name || '',
          quote_amount: quotes?.[0]?.total || 0
        }
      });
      setAiEmailDraft(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to draft email');
    } finally {
      setAiEmailLoading(false);
    }
  };

  const applyAIEmailDraft = () => {
    if (aiEmailDraft) {
      setEmailData({
        ...emailData,
        subject: aiEmailDraft.subject,
        body: aiEmailDraft.body,
        recipient: job?.client_email || ''
      });
      setAiEmailOpen(false);
      setEmailOpen(true);
      toast.success('AI draft applied!');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!jobData) return <div className="text-center py-12"><h2 className="text-xl text-muted-foreground">Job not found</h2><Button variant="link" onClick={() => navigate('/jobs')}>Back to Jobs</Button></div>;

  const { job, quotes, invoices, purchase_orders } = jobData;
  const StatusIcon = statusConfig[job.status]?.icon || Clock;
  const filteredPOItems = poQuote?.items?.filter(item => poTradeFilter === 'all' || item.trade_type === poTradeFilter) || [];
  const uniqueTrades = [...new Set(poQuote?.items?.map(item => item.trade_type).filter(Boolean))];
  const openEditJob = () => {
    setEditJobData({
      title: job.title || '',
      description: job.description || '',
      client_name: job.client_name || '',
      client_email: job.client_email || '',
      client_phone: job.client_phone || '',
      address: job.address || '',
      supplier_name: job.supplier_name || '',
      po_reference: job.po_reference || '',
      on_site_date: (job.on_site_date || '').slice(0, 10),
      start_date: (job.start_date || '').slice(0, 10),
      end_date: (job.end_date || '').slice(0, 10),
      budget: job.budget ?? '',
      trades_required: (job.trades_required || []).join(', '),
      status: job.status || 'pending',
    });
    setEditJobOpen(true);
  };

  const handleSaveJob = async () => {
    setSavingJob(true);
    try {
      const payload = { ...editJobData };
      payload.budget = payload.budget === '' || payload.budget === null ? null : parseFloat(payload.budget);
      payload.trades_required = (editJobData.trades_required || '')
        .split(',').map((t) => t.trim()).filter(Boolean);
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      await jobsAPI.update(jobId, payload);
      toast.success('Job updated');
      setEditJobOpen(false);
      fetchJobData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update job');
    } finally {
      setSavingJob(false);
    }
  };

  const totals = calculateQuoteTotal();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{job.title}</h1>
          <p className="text-muted-foreground">{job.address}</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig[job.status]?.color}`}>
          <StatusIcon className="h-4 w-4" />
          <span className="text-sm font-medium">{statusConfig[job.status]?.label}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={openEditJob} variant="outline" size="sm" data-testid="edit-job-btn"><Edit className="h-4 w-4 mr-1" /> Edit Job</Button>
        <Button onClick={() => setQuoteOpen(true)} size="sm"><Plus className="h-4 w-4 mr-1" /> New Quote</Button>
        <Button onClick={() => setEmailOpen(true)} variant="outline" size="sm"><Mail className="h-4 w-4 mr-1" /> Send Email</Button>
        <div className="border-l border-border mx-2"></div>
        <Button 
          onClick={handleAISuggestTrades} 
          variant="outline" 
          size="sm" 
          disabled={aiSuggestLoading}
          className="border-primary/50 hover:border-primary"
        >
          {aiSuggestLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserCheck className="h-4 w-4 mr-1 text-primary" />}
          AI Trade Match
        </Button>
        <Button 
          onClick={handleAICostEstimate} 
          variant="outline" 
          size="sm" 
          disabled={aiCostLoading}
          className="border-primary/50 hover:border-primary"
        >
          {aiCostLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Brain className="h-4 w-4 mr-1 text-primary" />}
          AI Cost Estimate
        </Button>
        <Button 
          onClick={() => { setAiEmailOpen(true); setAiEmailRecipient(job.client_name || ''); }}
          variant="outline" 
          size="sm"
          className="border-primary/50 hover:border-primary"
        >
          <Wand2 className="h-4 w-4 mr-1 text-primary" />
          AI Draft Email
        </Button>
      </div>

      {/* Edit Job Dialog */}
      <Dialog open={editJobOpen} onOpenChange={setEditJobOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-job-dialog">
          <DialogHeader><DialogTitle>Edit Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Title</Label>
              <Input value={editJobData.title || ''} onChange={(e) => setEditJobData({ ...editJobData, title: e.target.value })} data-testid="edit-job-title" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={editJobData.description || ''} onChange={(e) => setEditJobData({ ...editJobData, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Client name</Label>
                <Input value={editJobData.client_name || ''} onChange={(e) => setEditJobData({ ...editJobData, client_name: e.target.value })} data-testid="edit-job-client" />
              </div>
              <div className="space-y-1">
                <Label>Client email</Label>
                <Input value={editJobData.client_email || ''} onChange={(e) => setEditJobData({ ...editJobData, client_email: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Client phone</Label>
                <Input value={editJobData.client_phone || ''} onChange={(e) => setEditJobData({ ...editJobData, client_phone: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Site address</Label>
                <Input value={editJobData.address || ''} onChange={(e) => setEditJobData({ ...editJobData, address: e.target.value })} data-testid="edit-job-address" />
              </div>
              <div className="space-y-1">
                <Label>Supplier / builder</Label>
                <Input value={editJobData.supplier_name || ''} onChange={(e) => setEditJobData({ ...editJobData, supplier_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>PO reference</Label>
                <Input value={editJobData.po_reference || ''} onChange={(e) => setEditJobData({ ...editJobData, po_reference: e.target.value })} data-testid="edit-job-po-ref" />
              </div>
              <div className="space-y-1">
                <Label>On-site date</Label>
                <Input type="date" value={editJobData.on_site_date || ''} onChange={(e) => setEditJobData({ ...editJobData, on_site_date: e.target.value })} data-testid="edit-job-onsite" />
              </div>
              <div className="space-y-1">
                <Label>Budget ($)</Label>
                <Input type="number" value={editJobData.budget ?? ''} onChange={(e) => setEditJobData({ ...editJobData, budget: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Start date</Label>
                <Input type="date" value={editJobData.start_date || ''} onChange={(e) => setEditJobData({ ...editJobData, start_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>End date</Label>
                <Input type="date" value={editJobData.end_date || ''} onChange={(e) => setEditJobData({ ...editJobData, end_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={editJobData.status} onValueChange={(v) => setEditJobData({ ...editJobData, status: v })}>
                  <SelectTrigger data-testid="edit-job-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['pending', 'in_progress', 'completed', 'on_hold', 'cancelled', 'invoiced'].map((s) => (
                      <SelectItem key={s} value={s}>{statusConfig[s]?.label || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Trades required (comma-separated)</Label>
              <Input value={editJobData.trades_required || ''} onChange={(e) => setEditJobData({ ...editJobData, trades_required: e.target.value })} placeholder="roofing, plastering, painting" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditJobOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveJob} disabled={savingJob} data-testid="save-job-btn">
              {savingJob ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="pos">POs ({purchase_orders.length})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="emails">Emails</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="bg-card border-border lg:col-span-2">
              <CardHeader><CardTitle className="text-lg">Job Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {job.description && <div><label className="text-sm text-muted-foreground">Description</label><p className="mt-1">{job.description}</p></div>}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Address</p><p>{job.address}</p></div></div>
                  <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Budget</p><p>${job.budget?.toLocaleString() || 'N/A'}</p></div></div>
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Start Date</p><p>{formatDate(job.start_date)}</p></div></div>
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">End Date</p><p>{formatDate(job.end_date)}</p></div></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardHeader><CardTitle className="text-lg">Client</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /><span>{job.client_name}</span></div>
                {job.client_email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><a href={`mailto:${job.client_email}`} className="text-primary hover:underline">{job.client_email}</a></div>}
                {job.client_phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span>{job.client_phone}</span></div>}
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Quotes</p><p className="text-2xl font-bold">{quotes.length}</p></CardContent></Card>
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Invoices</p><p className="text-2xl font-bold">{invoices.length}</p></CardContent></Card>
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Purchase Orders</p><p className="text-2xl font-bold">{purchase_orders.length}</p></CardContent></Card>
            <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-sm text-muted-foreground">Contractors</p><p className="text-2xl font-bold">{jobData.contractors?.length || 0}</p></CardContent></Card>
          </div>
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Quotes</CardTitle>
              <div className="flex items-center gap-2">
                {job.po_reference && (
                  <Button size="sm" variant="outline" onClick={handleCreateQuoteFromPO} disabled={creatingQuoteFromPO} data-testid="create-quote-from-po-btn">
                    <Sparkles className="h-4 w-4 mr-1" /> {creatingQuoteFromPO ? 'Creating…' : 'Create quote from PO'}
                  </Button>
                )}
                <Button size="sm" onClick={() => setQuoteOpen(true)} data-testid="new-quote-btn"><Plus className="h-4 w-4 mr-1" /> New Quote</Button>
              </div>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><FileText className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No quotes yet</p>{job.po_reference && (<Button size="sm" variant="outline" className="mt-4" onClick={handleCreateQuoteFromPO} disabled={creatingQuoteFromPO} data-testid="create-quote-from-po-empty-btn"><Sparkles className="h-4 w-4 mr-1" /> {creatingQuoteFromPO ? 'Creating…' : 'Create draft quote from PO'}</Button>)}</div>
              ) : (
                <div className="space-y-3">
                  {quotes.map(q => (
                    <div key={q.id} className="border border-border rounded-lg p-4 hover:bg-secondary/20" data-testid={`quote-card-${q.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{q.quote_number}</h4>
                            {q.revision_number > 0 && (
                              <Badge variant="outline" className="text-xs">REV {q.revision_number}</Badge>
                            )}
                            {q.name && <span className="text-sm text-muted-foreground">({q.name})</span>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {q.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || q.items?.length || 0} items • Created {formatDate(q.created_at)}
                            {q.variation_of && <span className="ml-1">• Variation</span>}
                          </p>
                          <p className="text-lg font-bold mt-1">${q.total?.toFixed(2)}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${statusConfig[q.status]?.color}`}>{statusConfig[q.status]?.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => handleEditQuote(q)} data-testid={`edit-quote-${q.id}`}>
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleViewQuote(q)}><Eye className="h-4 w-4 mr-1" /> View</Button>
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(q.id, 'quote')}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                        <Button size="sm" variant="outline" onClick={() => handleDuplicateQuote(q)}>
                          <Copy className="h-4 w-4 mr-1" /> Duplicate
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleCreateRevision(q)}>
                          <RefreshCw className="h-4 w-4 mr-1" /> New Rev
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openVariationDialog(q)}>
                          <Sparkles className="h-4 w-4 mr-1" /> Variation
                        </Button>
                        <Button size="sm" onClick={() => openPODialog(q)}><ShoppingCart className="h-4 w-4 mr-1" /> Generate PO</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Purchase Orders Tab */}
        <TabsContent value="pos">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-lg">Purchase Orders</CardTitle></CardHeader>
            <CardContent>
              {purchase_orders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No purchase orders yet</p></div>
              ) : (
                <div className="space-y-3">
                  {purchase_orders.map(po => (
                    <div key={po.id} className={`border border-border rounded-lg p-4 ${po.status === 'cancelled' ? 'opacity-60' : 'hover:bg-secondary/20'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{po.po_number}</h4>
                          <p className="text-sm text-muted-foreground">{po.contractor_name || 'Unknown Contractor'}</p>
                          <p className="text-lg font-bold mt-1">${po.total?.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs ${statusConfig[po.status]?.color}`}>{statusConfig[po.status]?.label}</span>
                          {po.cancelled_at && <p className="text-xs text-muted-foreground mt-1">Cancelled {formatDate(po.cancelled_at)}</p>}
                        </div>
                      </div>
                      {po.status !== 'cancelled' && (
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(po.id, 'po')}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                          <Button size="sm" variant="destructive" onClick={() => { setCancellingPo(po); setCancelPoOpen(true); }}><Ban className="h-4 w-4 mr-1" /> Cancel</Button>
                        </div>
                      )}
                      {po.cancelled_reason && <p className="text-xs text-muted-foreground mt-2 italic">Reason: {po.cancelled_reason}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Invoices</CardTitle>
              <div className="flex items-center gap-2">
                <input type="file" ref={invFileRef} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt" onChange={handleUploadInvoiceFile} data-testid="upload-invoice-input" />
                <Button size="sm" variant="outline" onClick={() => invFileRef.current?.click()} disabled={uploadingInv} data-testid="upload-invoice-btn">
                  {uploadingInv ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} {uploadingInv ? 'Reading…' : 'Upload invoice'}
                </Button>
                <Button size="sm" onClick={() => { setLogInvData({ invoice_number: '', invoice_date: new Date().toISOString().slice(0,10), subtotal: '', gst: '', total: '', notes: '' }); setLogInvOpen(true); }} data-testid="log-invoice-btn"><Plus className="h-4 w-4 mr-1" /> Log invoice</Button>
              </div>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No invoices yet</p></div>
              ) : (
                <div className="space-y-3">
                  {invoices.map(inv => (
                    <div key={inv.id} className="border border-border rounded-lg p-4 hover:bg-secondary/20" data-testid={`job-invoice-${inv.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{inv.invoice_number}</h4>
                          <p className="text-sm text-muted-foreground">Due: {formatDate(inv.due_date)}</p>
                          <p className="text-lg font-bold mt-1">${inv.total?.toFixed(2)}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${statusConfig[inv.status]?.color}`}>{statusConfig[inv.status]?.label || inv.status}</span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => handleViewInvoice(inv)}><Eye className="h-4 w-4 mr-1" /> View</Button>
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(inv.id, 'invoice')}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Job Expenses</h3>
              <p className="text-sm text-muted-foreground">Track expenses for tax and budget purposes</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExportExpenses('csv')} disabled={expenses.length === 0}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
              <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="h-4 w-4 mr-2" /> Add Expense</Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add Expense</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleExpenseSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <select
                          value={expenseData.category}
                          onChange={(e) => setExpenseData({ ...expenseData, category: e.target.value })}
                          className="w-full bg-input border border-border rounded-lg px-3 py-2"
                        >
                          {expenseCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          value={expenseData.date}
                          onChange={(e) => setExpenseData({ ...expenseData, date: e.target.value })}
                          className="bg-input border-border rounded-lg"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Input
                        value={expenseData.description}
                        onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
                        placeholder="e.g., Timber for framing"
                        className="bg-input border-border rounded-lg"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount (Inc GST) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={expenseData.amount}
                          onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                          placeholder="0.00"
                          className="bg-input border-border rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Vendor</Label>
                        <Input
                          value={expenseData.vendor}
                          onChange={(e) => setExpenseData({ ...expenseData, vendor: e.target.value })}
                          placeholder="e.g., Bunnings"
                          className="bg-input border-border rounded-lg"
                        />
                      </div>
                    </div>
                    <div className="flex gap-6">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="gst_included"
                          checked={expenseData.gst_included}
                          onCheckedChange={(checked) => setExpenseData({ ...expenseData, gst_included: checked })}
                        />
                        <label htmlFor="gst_included" className="text-sm">GST Included</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="tax_deductible"
                          checked={expenseData.tax_deductible}
                          onCheckedChange={(checked) => setExpenseData({ ...expenseData, tax_deductible: checked })}
                        />
                        <label htmlFor="tax_deductible" className="text-sm">Tax Deductible</label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Receipt</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setExpenseReceipt(e.target.files[0])}
                        className="bg-input border-border rounded-lg"
                      />
                      {expenseReceipt && <p className="text-xs text-muted-foreground">{expenseReceipt.name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={expenseData.notes}
                        onChange={(e) => setExpenseData({ ...expenseData, notes: e.target.value })}
                        className="bg-input border-border rounded-lg"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => { setExpenseOpen(false); resetExpenseForm(); }}>Cancel</Button>
                      <Button type="submit">Add Expense</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Expense Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold">${expenseTotals.grand_total?.toFixed(2) || '0.00'}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">GST Claimable</p>
                <p className="text-2xl font-bold text-green-500">${expenseTotals.gst_total?.toFixed(2) || '0.00'}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Net Amount</p>
                <p className="text-2xl font-bold">${expenseTotals.net_total?.toFixed(2) || '0.00'}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Expense Count</p>
                <p className="text-2xl font-bold">{expenses.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Expense Table */}
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {expenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Receipt className="h-12 w-12 mb-4 opacity-50" />
                  <p>No expenses recorded yet</p>
                  <Button variant="link" onClick={() => setExpenseOpen(true)}>Add your first expense</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead>Tax</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map(exp => (
                      <TableRow key={exp.id}>
                        <TableCell>{formatDate(exp.date)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">{exp.category.replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{exp.description}</TableCell>
                        <TableCell>{exp.vendor || '-'}</TableCell>
                        <TableCell className="text-right font-medium">${exp.amount?.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">${exp.gst_amount?.toFixed(2)}</TableCell>
                        <TableCell>
                          {exp.tax_deductible ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(exp.id)} className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-1 overflow-x-auto pb-2">
              {jobDocumentTabs.map(tab => (
                <Button key={tab.id} variant={activeDocTab === tab.id ? 'default' : 'ghost'} size="sm" onClick={() => setActiveDocTab(tab.id)} className="whitespace-nowrap" data-testid={`doc-tab-${tab.id}`}>{tab.label}</Button>
              ))}
            </div>
            <Button onClick={() => setUploadOpen(true)} size="sm"><Upload className="h-4 w-4 mr-2" />Upload</Button>
          </div>
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mb-4 opacity-50" /><p>No documents in this category</p>
                  <Button variant="link" onClick={() => setUploadOpen(true)}>Upload a document</Button>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {documents.map(doc => (
                    <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-secondary/20">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.mime_type, doc.original_filename)}
                        <div>
                          <p className="font-medium">{doc.original_filename}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}{doc.uploaded_by === 'trade' ? ' • uploaded by trade' : ''}</p>
                          {doc.invoice_match && doc.invoice_match.status && (
                            <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border ${
                              doc.invoice_match.status === 'overcharge' ? 'bg-red-500/20 text-red-400 border-red-500/40'
                              : doc.invoice_match.status === 'matched' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              : 'bg-amber-400/15 text-amber-300 border-amber-400/30'}`}>
                              {doc.invoice_match.status === 'overcharge'
                                ? `⚠ Invoice over PO by $${Math.abs(doc.invoice_match.variance || 0).toLocaleString(undefined,{maximumFractionDigits:2})}`
                                : doc.invoice_match.status === 'matched'
                                ? `✅ Matches PO ($${Number(doc.invoice_match.po_total||0).toLocaleString()})`
                                : doc.invoice_match.status === 'under' ? 'Under PO' : 'Invoice — needs check'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}><Download className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-lg">Activity Log</CardTitle></CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><Activity className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No activity recorded yet</p></div>
              ) : (
                <div className="space-y-4">
                  {activity.map(item => (
                    <div key={item.id} className="flex items-start gap-3 pb-4 border-b border-border last:border-0">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0"><Activity className="h-4 w-4 text-primary" /></div>
                      <div className="flex-1">
                        <p className="text-sm"><span className="font-medium">{item.user_name}</span> {item.action} <span className="text-primary">{item.entity_type}</span></p>
                        {item.details && <p className="text-xs text-muted-foreground mt-1">{item.details}</p>}
                        <p className="text-xs text-muted-foreground mt-1">{formatDateTime(item.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emails Tab */}
        <TabsContent value="emails">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Job Emails</CardTitle>
              <Button size="sm" onClick={() => setEmailOpen(true)}><Send className="h-4 w-4 mr-2" />Send Email</Button>
            </CardHeader>
            <CardContent>
              {emails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground"><Mail className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No emails sent for this job yet</p></div>
              ) : (
                <div className="divide-y divide-border">
                  {emails.map(email => (
                    <div key={email.id} className="py-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{email.subject}</p>
                        <span className={`px-2 py-0.5 rounded text-xs ${email.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{email.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">To: {email.recipient_email}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDateTime(email.sent_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Invoice Dialog */}
      <Dialog open={logInvOpen} onOpenChange={setLogInvOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle>Log an invoice against this job</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Invoice number</Label>
                <Input value={logInvData.invoice_number} onChange={(e) => setLogInvData({ ...logInvData, invoice_number: e.target.value })} placeholder="Auto if blank" data-testid="log-inv-number" />
              </div>
              <div className="space-y-1.5">
                <Label>Invoice date</Label>
                <Input type="date" value={logInvData.invoice_date} onChange={(e) => setLogInvData({ ...logInvData, invoice_date: e.target.value })} data-testid="log-inv-date" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Subtotal (ex GST)</Label>
                <Input type="number" step="0.01" value={logInvData.subtotal} onChange={(e) => { const s = e.target.value; const sub = parseFloat(s) || 0; setLogInvData({ ...logInvData, subtotal: s, gst: (Math.round(sub * 0.1 * 100) / 100).toString(), total: (Math.round(sub * 1.1 * 100) / 100).toString() }); }} data-testid="log-inv-subtotal" />
              </div>
              <div className="space-y-1.5">
                <Label>GST</Label>
                <Input type="number" step="0.01" value={logInvData.gst} onChange={(e) => setLogInvData({ ...logInvData, gst: e.target.value })} data-testid="log-inv-gst" />
              </div>
              <div className="space-y-1.5">
                <Label>Total (inc GST)</Label>
                <Input type="number" step="0.01" value={logInvData.total} onChange={(e) => setLogInvData({ ...logInvData, total: e.target.value })} data-testid="log-inv-total" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Input value={logInvData.notes} onChange={(e) => setLogInvData({ ...logInvData, notes: e.target.value })} placeholder="e.g. Progress claim 1" data-testid="log-inv-notes" />
            </div>
            <p className="text-xs text-muted-foreground">Due date is set automatically from this supplier's payment terms (default 30 days).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogInvOpen(false)}>Cancel</Button>
            <Button onClick={handleLogInvoice} disabled={savingLogInv} data-testid="log-inv-save">
              {savingLogInv ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle>Upload to {jobDocumentTabs.find(t => t.id === activeDocTab)?.label}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="block text-sm font-medium mb-2">File</label><Input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="bg-background" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" /></div>
            <div><label className="block text-sm font-medium mb-2">Description</label><Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Brief description..." className="bg-background" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button><Button onClick={handleUpload} disabled={uploading || !uploadFile}>{uploading ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle>Send Email - {job.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="block text-sm font-medium mb-2">Template</label>
              <Select value={emailData.template} onValueChange={handleTemplateChange}>
                <SelectTrigger className="bg-background"><SelectValue placeholder="Select template or custom" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Email</SelectItem>
                  <SelectItem value="quote_sent">Quote Sent</SelectItem>
                  <SelectItem value="invoice_due">Invoice Due</SelectItem>
                  <SelectItem value="job_update">Job Update</SelectItem>
                  <SelectItem value="po_sent">PO Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="block text-sm font-medium mb-2">To</label><Input value={emailData.recipient} onChange={(e) => setEmailData({ ...emailData, recipient: e.target.value })} placeholder="recipient@email.com" className="bg-background" /></div>
            <div><label className="block text-sm font-medium mb-2">Subject</label><Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })} className="bg-background" /></div>
            <div><label className="block text-sm font-medium mb-2">Message</label><Textarea value={emailData.body} onChange={(e) => setEmailData({ ...emailData, body: e.target.value })} rows={6} className="bg-background" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button><Button onClick={handleSendEmail} disabled={emailSending}>{emailSending ? 'Sending...' : 'Send Email'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Creation/Edit Dialog with Sections */}
      <Dialog open={quoteOpen} onOpenChange={handleQuoteDialogClose}>
        <DialogContent className="bg-card border-border max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuote ? `Edit Quote ${editingQuote.quote_number}` : `Create Quote - ${job.title}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Client: {job.client_name}</p>
            
            {/* Template Selector - only show for new quotes */}
            {!editingQuote && (
              <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Load Quote Template
                </label>
                <div className="flex gap-2">
                  <Select 
                    value={selectedTemplate} 
                    onValueChange={(templateId) => {
                      const template = quoteTemplates.find(t => t.id === templateId);
                      if (template && template.sections) {
                        // Load template sections into quote
                        const loadedSections = template.sections.map(section => ({
                          section_type: section.section_type || 'custom',
                          section_name: section.section_name || '',
                          items: section.items.map(item => ({
                            description: item.description || '',
                            quantity: item.quantity || 1,
                            unit_price: item.unit_price || 0,
                            trade_type: item.trade_type || 'General',
                            unit: item.unit || 'Item',
                            buy_rate: item.buy_rate || 0
                          }))
                        }));
                        setQuoteSectionsData(loadedSections);
                        setSelectedTemplate(templateId);
                        toast.success(`Loaded template: ${template.name}`);
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1 bg-background">
                      <SelectValue placeholder="Select a template to pre-populate quote..." />
                    </SelectTrigger>
                    <SelectContent>
                      {quoteTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center justify-between gap-4">
                            <span className="font-medium">{template.name}</span>
                            <span className="text-xs text-muted-foreground">{template.category}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setQuoteSectionsData([{ section_type: 'preliminaries', section_name: '', items: [{ description: '', quantity: 1, unit_price: 0, trade_type: 'General' }] }]);
                      setSelectedTemplate('');
                      toast.success('Quote cleared');
                    }}
                  >
                    Clear
                  </Button>
                </div>
                {selectedTemplate && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Template loaded - all quantities and prices can be customized for this job
                  </p>
                )}
              </div>
            )}
            
            {/* Quick Add from Pricing Schedule */}
            <div className="p-3 bg-muted/50 rounded-lg border">
              <label className="block text-sm font-medium mb-2">Quick Add from Schedule of Rates</label>
              <div className="grid grid-cols-4 gap-2">
                <Select value={selectedPricingCategory} onValueChange={setSelectedPricingCategory}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Filter by category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {[...new Set(pricingItems.map(p => p.category).filter(Boolean))].sort().map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value="" 
                  onValueChange={(itemId) => {
                    const pricingItem = pricingItems.find(p => p.id === itemId);
                    if (pricingItem && quoteSectionsData.length > 0) {
                      // Map pricing category to trade type
                      let tradeType = 'General';
                      if (pricingItem.category?.includes('ELECTRIC')) tradeType = 'Electrician';
                      else if (pricingItem.category?.includes('PLASTER')) tradeType = 'Plasterer';
                      else if (pricingItem.category?.includes('CARPENTER')) tradeType = 'Carpenter';
                      else if (pricingItem.category?.includes('PAINT')) tradeType = 'Painter';
                      else if (pricingItem.category?.includes('PLUMB')) tradeType = 'Plumber';
                      else if (pricingItem.category?.includes('TILER')) tradeType = 'Tiler';
                      else if (pricingItem.category?.includes('DEMOL')) tradeType = 'Labourer';
                      else if (pricingItem.category?.includes('LABOUR')) tradeType = 'Labourer';
                      
                      const newItem = {
                        description: pricingItem.name + (pricingItem.description ? ` - ${pricingItem.description}` : ''),
                        quantity: 1,
                        unit_price: pricingItem.sell_rate || pricingItem.unit_price || 0,
                        trade_type: tradeType,
                        unit: pricingItem.unit || 'Item',
                        buy_rate: pricingItem.buy_rate || 0
                      };
                      // Add to last section
                      const lastSectionIdx = quoteSectionsData.length - 1;
                      const updatedSections = [...quoteSectionsData];
                      updatedSections[lastSectionIdx].items.push(newItem);
                      setQuoteSectionsData(updatedSections);
                      toast.success(`Added: ${pricingItem.name}`);
                    }
                  }}
                >
                  <SelectTrigger className="col-span-3 bg-background">
                    <SelectValue placeholder="Select item from pricing schedule..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {pricingItems
                      .filter(p => selectedPricingCategory === 'all' || p.category === selectedPricingCategory)
                      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
                      .map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <span className="truncate">
                              {item.code && <span className="text-muted-foreground mr-2">{item.code}</span>}
                              {item.name}
                            </span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              ${(item.sell_rate || item.unit_price || 0).toFixed(2)}/{item.unit || 'Item'}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
              <SortableContext items={quoteSectionsData.map((_, i) => `section-${i}`)} strategy={verticalListSortingStrategy}>
                <Accordion type="multiple" defaultValue={['section-0']} className="space-y-2">
                  {quoteSectionsData.map((section, sectionIdx) => (
                    <SortableSection key={`section-${sectionIdx}`} id={`section-${sectionIdx}`}>
                      <AccordionItem value={`section-${sectionIdx}`} className="border border-border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                            <span className="font-medium">
                              {section.section_type === 'custom' ? section.section_name || 'Custom Section' : sectionLabels[section.section_type]}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({section.items.length} items - ${section.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0).toFixed(2)})
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4 pt-2">
                            <div className="flex gap-2">
                              <Select value={section.section_type} onValueChange={(v) => updateSection(sectionIdx, 'section_type', v)}>
                                <SelectTrigger className="w-40 bg-background"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {quoteSections.map(s => <SelectItem key={s} value={s}>{sectionLabels[s] || s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              {section.section_type === 'custom' && (
                                <Input 
                                  placeholder="Custom section name" 
                                  value={section.section_name} 
                                  onChange={(e) => updateSection(sectionIdx, 'section_name', e.target.value)}
                                  className="bg-background"
                                />
                              )}
                              {quoteSectionsData.length > 1 && (
                                <Button variant="ghost" size="icon" onClick={() => removeSection(sectionIdx)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                              )}
                            </div>
                            
                            {/* Items in section with drag-and-drop */}
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd(sectionIdx)}>
                              <SortableContext items={section.items.map((_, i) => `item-${sectionIdx}-${i}`)} strategy={verticalListSortingStrategy}>
                                <div className="space-y-2">
                                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1">
                                    <div className="col-span-1"></div>
                                    <div className="col-span-3">Description</div>
                                    <div className="col-span-2">Trade</div>
                                    <div className="col-span-1">Unit</div>
                                    <div className="col-span-1">Qty</div>
                                    <div className="col-span-2">Price</div>
                                    <div className="col-span-1">Total</div>
                                    <div className="col-span-1"></div>
                                  </div>
                                  {section.items.map((item, itemIdx) => (
                                    <SortableItem key={`item-${sectionIdx}-${itemIdx}`} id={`item-${sectionIdx}-${itemIdx}`}>
                                      <div className="grid grid-cols-12 gap-2 items-center bg-background/50 p-1 rounded">
                                        <div className="col-span-1 flex justify-center">
                                          <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                                        </div>
                                        <div className="col-span-3">
                                          <Input value={item.description} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'description', e.target.value)} placeholder="Item description" className="bg-background text-sm" />
                                        </div>
                                        <div className="col-span-2">
                                          <Select value={item.trade_type} onValueChange={(v) => updateSectionItem(sectionIdx, itemIdx, 'trade_type', v)}>
                                            <SelectTrigger className="bg-background text-sm"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {tradeTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                              <SelectItem value="__add_new__" className="text-primary">
                                                <span className="flex items-center gap-1"><PlusCircle className="h-3 w-3" /> Add New Trade</span>
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="col-span-1">
                                          <Input value={item.unit || 'Item'} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'unit', e.target.value)} placeholder="Unit" className="bg-background text-sm" />
                                        </div>
                                        <div className="col-span-1">
                                          <Input type="number" value={item.quantity} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'quantity', e.target.value)} placeholder="Qty" className="bg-background text-sm" />
                                        </div>
                                        <div className="col-span-2">
                                          <Input type="number" value={item.unit_price} onChange={(e) => updateSectionItem(sectionIdx, itemIdx, 'unit_price', e.target.value)} placeholder="Price" className="bg-background text-sm" />
                                        </div>
                                        <div className="col-span-1 text-right font-mono text-sm">${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</div>
                                        <div className="col-span-1">
                                          <Button variant="ghost" size="icon" onClick={() => removeItemFromSection(sectionIdx, itemIdx)} disabled={section.items.length <= 1}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                      </div>
                                    </SortableItem>
                                  ))}
                                </div>
                              </SortableContext>
                            </DndContext>
                            
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => addItemToSection(sectionIdx)}><Plus className="h-4 w-4 mr-1" /> Add Blank Item</Button>
                              <Select 
                                value="" 
                                onValueChange={(itemId) => {
                                  const pricingItem = pricingItems.find(p => p.id === itemId);
                                  if (pricingItem) {
                                    let tradeType = 'General';
                                    if (pricingItem.category?.includes('ELECTRIC')) tradeType = 'Electrician';
                                    else if (pricingItem.category?.includes('PLASTER')) tradeType = 'Plasterer';
                                    else if (pricingItem.category?.includes('CARPENTER')) tradeType = 'Carpenter';
                                    else if (pricingItem.category?.includes('PAINT')) tradeType = 'Painter';
                                    else if (pricingItem.category?.includes('PLUMB')) tradeType = 'Plumber';
                                    else if (pricingItem.category?.includes('TILER')) tradeType = 'Tiler';
                                    else if (pricingItem.category?.includes('DEMOL')) tradeType = 'Labourer';
                                    else if (pricingItem.category?.includes('LABOUR')) tradeType = 'Labourer';
                                    
                                    const newItem = {
                                      description: pricingItem.name + (pricingItem.description ? ` - ${pricingItem.description}` : ''),
                                      quantity: 1,
                                      unit_price: pricingItem.sell_rate || pricingItem.unit_price || 0,
                                      trade_type: tradeType,
                                      unit: pricingItem.unit || 'Item',
                                      buy_rate: pricingItem.buy_rate || 0
                                    };
                                    const updatedSections = [...quoteSectionsData];
                                    updatedSections[sectionIdx].items.push(newItem);
                                    setQuoteSectionsData(updatedSections);
                                  }
                                }}
                              >
                                <SelectTrigger className="w-64 bg-background text-sm">
                                  <SelectValue placeholder="+ Add from Schedule of Rates" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {pricingItems.sort((a, b) => (a.category || '').localeCompare(b.category || '')).map(item => (
                                    <SelectItem key={item.id} value={item.id}>
                                      <span className="text-xs text-muted-foreground mr-1">{item.code}</span>
                                      {item.name} - ${(item.sell_rate || item.unit_price || 0).toFixed(2)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </SortableSection>
                  ))}
                </Accordion>
              </SortableContext>
            </DndContext>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={addSection}><Plus className="h-4 w-4 mr-1" /> Add Section</Button>
              <Button variant="outline" onClick={() => setAddTradeOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Add Custom Trade</Button>
            </div>
            
            <div className="border-t pt-3 text-right">
              <p className="text-sm">Subtotal: ${totals.subtotal.toFixed(2)}</p>
              <p className="text-sm">GST (10%): ${totals.tax.toFixed(2)}</p>
              <p className="font-bold text-lg">Total: ${totals.total.toFixed(2)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleQuoteDialogClose(false)}>Cancel</Button>
            <Button onClick={handleCreateQuote} data-testid="save-quote-btn">{editingQuote ? 'Update Quote' : 'Create Quote'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Trade Dialog */}
      <Dialog open={addTradeOpen} onOpenChange={setAddTradeOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Trade Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Add a custom trade type that will be available for all future jobs and quotes.
            </p>
            <div className="space-y-2">
              <Label>Trade Name</Label>
              <Input
                value={newTradeType}
                onChange={(e) => setNewTradeType(e.target.value)}
                placeholder="e.g., Solar Installer, Pool Builder..."
                className="bg-input border-border"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              <p>Existing trades: {tradeTypes.join(', ')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddTradeOpen(false); setNewTradeType(''); }}>Cancel</Button>
            <Button onClick={handleAddTradeType}>Add Trade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Variation Dialog */}
      <Dialog open={variationOpen} onOpenChange={setVariationOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Generate Quote Variation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Create a variation of <span className="font-semibold">{variationQuote?.quote_number}</span> with different pricing options.
            </p>
            
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm">Current Total: <span className="font-bold">${variationQuote?.total?.toFixed(2)}</span></p>
            </div>
            
            <div className="space-y-2">
              <Label>Variation Type</Label>
              <Select value={variationType} onValueChange={setVariationType}>
                <SelectTrigger className="bg-input border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="budget">
                    <div className="flex items-center gap-2">
                      <span>💰 Budget Option</span>
                      <span className="text-xs text-muted-foreground">(-15 to -25%)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="premium">
                    <div className="flex items-center gap-2">
                      <span>⭐ Premium Option</span>
                      <span className="text-xs text-muted-foreground">(+20 to +35%)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="minimal">
                    <div className="flex items-center gap-2">
                      <span>📋 Minimal Option</span>
                      <span className="text-xs text-muted-foreground">(-30 to -40%)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <span>⚙️ Custom Adjustment</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {variationType === 'custom' && (
              <div className="space-y-2">
                <Label>Adjustment Percentage</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={customAdjustment}
                    onChange={(e) => setCustomAdjustment(e.target.value)}
                    placeholder="e.g., -10 or +15"
                    className="bg-input border-border"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter positive or negative percentage (e.g., -10 for 10% off, +15 for 15% more)
                </p>
              </div>
            )}
            
            <div className="text-xs text-muted-foreground bg-primary/5 p-3 rounded-lg">
              <p className="font-medium mb-1">How variations work:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li><strong>Budget:</strong> AI suggests cost-effective alternatives</li>
                <li><strong>Premium:</strong> AI upgrades to higher-quality options</li>
                <li><strong>Minimal:</strong> Strips to essential items only</li>
                <li><strong>Custom:</strong> Simple percentage adjustment</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariationOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerateVariation} disabled={variationLoading || (variationType === 'custom' && !customAdjustment)}>
              {variationLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Generate Variation</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Quote Dialog */}
      <Dialog open={viewQuoteOpen} onOpenChange={setViewQuoteOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedQuote?.quote_number}</DialogTitle></DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status: <span className={`px-2 py-0.5 rounded ${statusConfig[selectedQuote.status]?.color}`}>{statusConfig[selectedQuote.status]?.label}</span></span>
                <span className="text-muted-foreground">Created: {formatDate(selectedQuote.created_at)}</span>
              </div>
              <div className="border rounded-lg p-4 space-y-2">
                {selectedQuote.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.description} {item.section_name && <span className="text-xs text-muted-foreground">({item.section_name})</span>}</span>
                    <span>${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm">Subtotal: ${selectedQuote.subtotal?.toFixed(2)}</p>
                <p className="text-sm">GST: ${selectedQuote.tax?.toFixed(2)}</p>
                <p className="font-bold">Total: ${selectedQuote.total?.toFixed(2)}</p>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewQuoteOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={viewInvoiceOpen} onOpenChange={setViewInvoiceOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle>{selectedInvoice?.invoice_number}</DialogTitle></DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status: <span className={`px-2 py-0.5 rounded ${statusConfig[selectedInvoice.status]?.color}`}>{statusConfig[selectedInvoice.status]?.label}</span></span>
                <span className="text-muted-foreground">Due: {formatDate(selectedInvoice.due_date)}</span>
              </div>
              <div className="border rounded-lg p-4 space-y-2">
                {selectedInvoice.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.description}</span>
                    <span>${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="text-right space-y-1">
                <p className="font-bold">Total: ${selectedInvoice.total?.toFixed(2)}</p>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewInvoiceOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO from Quote Dialog - Enhanced with Budget Tracking */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="bg-card border-border max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Generate Purchase Orders from Quote {poQuote?.quote_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            
            {/* Budget Summary - Always visible */}
            {(() => {
              const budget = getBudgetSummary();
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 bg-muted/50 rounded-lg border">
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Quote Total</div>
                    <div className="text-lg font-bold">${budget.quoteTotal.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Allocated to POs</div>
                    <div className="text-lg font-bold text-red-500">${budget.allocatedTotal.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Unallocated</div>
                    <div className={`text-lg font-bold ${budget.unallocatedTotal > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                      ${budget.unallocatedTotal.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Margin</div>
                    <div className={`text-lg font-bold ${parseFloat(budget.margin) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {budget.margin}%
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground">Items</div>
                    <div className={`text-lg font-bold ${budget.isFullyAllocated ? 'text-green-500' : 'text-yellow-500'}`}>
                      {budget.allocatedItems}/{budget.totalItems}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Contractor Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Contractor to Assign Items</label>
                <Select value={poContractor} onValueChange={setPoContractor}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Choose contractor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} - {Array.isArray(c.trade_types) ? c.trade_types.join(', ') : c.trade_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Filter Items by Trade</label>
                <Select value={poTradeFilter} onValueChange={setPoTradeFilter}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trades</SelectItem>
                    {uniqueTrades.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quote Items - Select and Assign */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Quote Items 
                <span className="font-normal text-muted-foreground ml-2">(click + to assign remaining qty to selected contractor)</span>
              </label>
              <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-2/5">Description</th>
                      <th className="text-left p-2">Trade</th>
                      <th className="text-center p-2">Remaining</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Total</th>
                      <th className="text-center p-2 w-16">Add</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(poQuote?.flatItems || poQuote?.items || [])
                      .filter(item => poTradeFilter === 'all' || item.trade_type === poTradeFilter)
                      .map((item, idx) => {
                        const remainingQty = getRemainingQty(item);
                        const isFullyAllocated = remainingQty <= 0;
                        
                        return (
                          <tr key={item.itemKey || idx} className={`border-t ${isFullyAllocated ? 'bg-green-500/10' : ''}`}>
                            <td className="p-2">
                              <div className="font-medium">{item.description}</div>
                              {item.section_name && <div className="text-xs text-muted-foreground">{item.section_name}</div>}
                            </td>
                            <td className="p-2">
                              <span className="px-2 py-0.5 bg-secondary rounded text-xs">{item.trade_type || 'General'}</span>
                            </td>
                            <td className="p-2 text-center">
                              {isFullyAllocated ? (
                                <span className="text-green-500 font-medium">✓ Done</span>
                              ) : (
                                <span className="font-mono">
                                  <span className="text-yellow-500 font-bold">{remainingQty}</span>
                                  <span className="text-muted-foreground"> / {item.quantity || 1}</span>
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right font-mono">${(item.unit_price || 0).toFixed(2)}</td>
                            <td className="p-2 text-right font-mono">${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</td>
                            <td className="p-2 text-center">
                              {!isFullyAllocated && (
                                <Button 
                                  size="icon" 
                                  variant="outline" 
                                  className="h-7 w-7 hover:bg-primary hover:text-primary-foreground border-primary/50"
                                  onClick={() => addItemAllocation(item, remainingQty)}
                                  disabled={!poContractor}
                                  title={poContractor ? `Add ${remainingQty} to contractor` : 'Select a contractor first'}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Current Allocations - Grouped by Contractor */}
            {itemAllocations.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  PO Allocations 
                  <span className="font-normal text-muted-foreground ml-2">(each contractor = 1 PO)</span>
                </label>
                
                {/* Group allocations by contractor */}
                {Object.entries(getAllocationTotals()).map(([contractorId, data]) => {
                  const contractorAllocations = itemAllocations.filter(a => a.contractor_id === contractorId);
                  const trades = [...data.trades].join(', ');
                  
                  return (
                    <div key={contractorId} className="mb-3 border rounded-lg overflow-hidden">
                      <div className="bg-primary/10 p-2 flex items-center justify-between">
                        <div>
                          <span className="font-bold">{data.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">({trades})</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-primary">${data.total.toFixed(2)}</span>
                          <span className="text-xs text-muted-foreground ml-2">{data.items} item(s)</span>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {contractorAllocations.map((alloc, idx) => {
                            const globalIdx = itemAllocations.findIndex(a => a === alloc);
                            return (
                              <tr key={idx} className="border-t">
                                <td className="p-2 w-1/2">{alloc.description}</td>
                                <td className="p-2 w-20">
                                  <Input 
                                    type="number" 
                                    value={alloc.quantity} 
                                    onChange={(e) => updateAllocation(globalIdx, 'quantity', e.target.value)}
                                    className="h-7 w-16 bg-background text-right"
                                    min="0.01"
                                    step="0.01"
                                  />
                                </td>
                                <td className="p-2 w-24">
                                  <Input 
                                    type="number" 
                                    value={alloc.unit_price} 
                                    onChange={(e) => updateAllocation(globalIdx, 'unit_price', e.target.value)}
                                    className="h-7 w-20 bg-background text-right"
                                    min="0"
                                    step="0.01"
                                  />
                                </td>
                                <td className="p-2 w-24 text-right font-mono font-medium">
                                  ${(alloc.quantity * alloc.unit_price).toFixed(2)}
                                </td>
                                <td className="p-2 w-12 text-center">
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    className="h-7 w-7 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                    onClick={() => removeAllocation(globalIdx)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Warning if not fully allocated */}
            {(() => {
              const budget = getBudgetSummary();
              if (itemAllocations.length > 0 && !budget.isFullyAllocated) {
                return (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                    <span className="text-sm">
                      <strong>{budget.unallocatedItems} item(s)</strong> not yet allocated (${budget.unallocatedTotal.toFixed(2)} unassigned)
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            {/* Additional Items */}
            <div>
              <label className="block text-sm font-medium mb-2">Add Extra Items (to selected contractor)</label>
              <Select onValueChange={(v) => { const item = commonPOItems.find(i => i.description === v); if (item) addAdditionalItem(item); }}>
                <SelectTrigger className="bg-background"><SelectValue placeholder="Add common items..." /></SelectTrigger>
                <SelectContent>{commonPOItems.map(item => <SelectItem key={item.description} value={item.description}>{item.description}</SelectItem>)}</SelectContent>
              </Select>
              {additionalItems.length > 0 && (
                <div className="mt-2 space-y-1">
                  {additionalItems.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-secondary/30 p-2 rounded">
                      <span>{item.description}</span>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={item.quantity} onChange={(e) => { const updated = [...additionalItems]; updated[idx].quantity = parseFloat(e.target.value) || 1; setAdditionalItems(updated); }} className="w-16 h-7 bg-background" />
                        <Input type="number" value={item.unit_price} onChange={(e) => { const updated = [...additionalItems]; updated[idx].unit_price = parseFloat(e.target.value) || 0; setAdditionalItems(updated); }} className="w-20 h-7 bg-background" />
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAdditionalItems(additionalItems.filter((_, i) => i !== idx))}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoOpen(false)}>Cancel</Button>
            <Button onClick={handleGeneratePO} disabled={itemAllocations.length === 0}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              Generate {Object.keys(getAllocationTotals()).length} PO(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel PO Dialog */}
      <Dialog open={cancelPoOpen} onOpenChange={setCancelPoOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /> Cancel Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">Are you sure you want to cancel <strong>{cancellingPo?.po_number}</strong>?</p>
            <div>
              <label className="block text-sm font-medium mb-2">Reason for Cancellation</label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Enter reason..." className="bg-background" rows={3} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="sendEmail" checked={sendCancelEmail} onChange={(e) => setSendCancelEmail(e.target.checked)} className="rounded" />
              <label htmlFor="sendEmail" className="text-sm">Send cancellation email to contractor</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelPoOpen(false)}>Keep PO</Button>
            <Button variant="destructive" onClick={handleCancelPO}>Cancel PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Trade Suggestions Dialog */}
      <Dialog open={aiSuggestOpen} onOpenChange={setAiSuggestOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Trade Recommendations
            </DialogTitle>
          </DialogHeader>
          {aiTradeSuggestions && (
            <div className="space-y-4 py-4">
              {aiTradeSuggestions.required_trades?.length > 0 && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium mb-1">Trades Needed for this Job:</p>
                  <div className="flex flex-wrap gap-2">
                    {aiTradeSuggestions.required_trades.map((trade, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary/20 text-primary rounded text-sm">{trade}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <p className="text-sm font-medium">Recommended Contractors:</p>
                {aiTradeSuggestions.suggestions?.map((suggestion, idx) => (
                  <Card key={idx} className="bg-secondary/30 border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{suggestion.contractor_name}</h4>
                          <p className="text-sm text-muted-foreground">{suggestion.trade_types?.join(', ')}</p>
                        </div>
                        <div className="text-right">
                          <div className={`px-2 py-1 rounded text-sm ${
                            suggestion.match_score >= 80 ? 'bg-green-500/20 text-green-400' :
                            suggestion.match_score >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {suggestion.match_score}% Match
                          </div>
                          <span className={`text-xs ${
                            suggestion.availability === 'high' ? 'text-green-400' :
                            suggestion.availability === 'medium' ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {suggestion.availability} availability
                          </span>
                        </div>
                      </div>
                      <p className="text-sm mt-2 text-muted-foreground">{suggestion.reason}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {aiTradeSuggestions.notes && (
                <p className="text-sm text-muted-foreground italic">{aiTradeSuggestions.notes}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiSuggestOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Cost Estimate Dialog */}
      <Dialog open={aiCostOpen} onOpenChange={setAiCostOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              AI Cost Estimate
            </DialogTitle>
          </DialogHeader>
          {aiCostEstimate && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                <span className="text-lg font-medium">Estimated Total</span>
                <span className="text-2xl font-bold text-primary">${aiCostEstimate.estimated_total?.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Confidence:</span>
                <span className={`px-2 py-1 rounded text-sm ${
                  aiCostEstimate.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                  aiCostEstimate.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {aiCostEstimate.confidence}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Cost Breakdown:</p>
                <div className="space-y-2">
                  {aiCostEstimate.breakdown?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-secondary/30 rounded text-sm">
                      <div>
                        <span className="font-medium">{item.description}</span>
                        <span className="text-muted-foreground ml-2">({item.category})</span>
                      </div>
                      <span className="font-mono">${item.estimated_cost?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              {aiCostEstimate.risk_factors?.length > 0 && (
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <p className="text-sm font-medium text-yellow-400 mb-1">Risk Factors:</p>
                  <ul className="text-sm list-disc list-inside text-muted-foreground">
                    {aiCostEstimate.risk_factors.map((risk, idx) => <li key={idx}>{risk}</li>)}
                  </ul>
                </div>
              )}
              {aiCostEstimate.recommendations?.length > 0 && (
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <p className="text-sm font-medium text-blue-400 mb-1">Recommendations:</p>
                  <ul className="text-sm list-disc list-inside text-muted-foreground">
                    {aiCostEstimate.recommendations.map((rec, idx) => <li key={idx}>{rec}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiCostOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Email Draft Dialog */}
      <Dialog open={aiEmailOpen} onOpenChange={(open) => { setAiEmailOpen(open); if (!open) { setAiEmailDraft(null); setAiEmailRecipient(''); } }}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              AI Email Assistant
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Type</label>
              <Select value={aiEmailType} onValueChange={setAiEmailType}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote_followup">Quote Follow-up</SelectItem>
                  <SelectItem value="payment_reminder">Payment Reminder</SelectItem>
                  <SelectItem value="job_update">Job Update</SelectItem>
                  <SelectItem value="thank_you">Thank You</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Recipient Name</label>
              <Input 
                value={aiEmailRecipient} 
                onChange={(e) => setAiEmailRecipient(e.target.value)}
                placeholder="e.g., Mr. Smith"
                className="bg-background"
              />
            </div>
            {aiEmailDraft && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">Subject:</span>
                    <p className="font-medium">{aiEmailDraft.subject}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Body:</span>
                    <p className="text-sm whitespace-pre-wrap mt-1">{aiEmailDraft.body}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Tone: {aiEmailDraft.tone}</span>
                    <span>Follow-up in: {aiEmailDraft.suggested_followup_days} days</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiEmailOpen(false)}>Cancel</Button>
            {!aiEmailDraft ? (
              <Button onClick={handleAIDraftEmail} disabled={aiEmailLoading || !aiEmailRecipient}>
                {aiEmailLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Wand2 className="h-4 w-4 mr-2" />Generate Draft</>}
              </Button>
            ) : (
              <Button onClick={applyAIEmailDraft} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />Use This Draft
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetailPage;
